export type Subscription<T = any> = Promise<T> & { unsubscribe: () => void };

/**
 * The backend returns RFC 7807 ProblemDetails (application/problem+json) for every error:
 * `{ type, title, status, detail, instance, traceId, errors? }`. `errors` is present on a
 * ValidationProblemDetails (field -> messages). This extracts the most useful human message,
 * tolerating a legacy raw-string body or `{ Message }` shape during the transition.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
  Message?: string; // legacy shape, tolerated
}

export function getProblemMessage(
  error: unknown,
  fallback = "Something went wrong, try again",
): string {
  const data = (error as any)?.response?.data;
  if (data == null) return fallback;

  if (typeof data === "string") return data.trim() || fallback;

  const problem = data as ProblemDetails;

  if (problem.errors && typeof problem.errors === "object") {
    const first = Object.values(problem.errors)
      .flat()
      .find((m) => typeof m === "string" && m.trim().length > 0);
    if (first) return first;
  }

  return problem.detail || problem.title || problem.Message || fallback;
}

// Audit fix 11 (2026-11-04) — previously retried forever on any error,
// spinning a 10s cycle against a dead endpoint until the browser tab died.
// Now capped at 3 attempts (≈20s total) then the last error propagates so
// the caller can show a real error state.
export function retry<T = any>(
  fn: (count: number) => Promise<T>,
  delay: number = 10000,
  maxAttempts: number = 3,
): Promise<T> {
  let count = 0;
  const f = (
    fn: (count: number) => Promise<T>,
    delay: number,
  ): Promise<T> => {
    return fn(count).catch((err) => {
      ++count;
      if (count >= maxAttempts) {
        return Promise.reject(err);
      }
      return new Promise<T>((resolve, reject) => {
        setTimeout(() => {
          f(fn, delay).then(resolve, reject);
        }, delay);
      });
    });
  };

  return f(fn, delay);
}

export function subscription<T = any>(
  fn: () => Promise<T>,
  delay = 250,
): Subscription<T> {
  let cancelled = false;
  const promise = new Promise<T>((resolve, reject) => {
    setTimeout(() => {
      if (!cancelled) {
        fn()
          .then((result) => {
            if (!cancelled) {
              resolve(result);
            }
          })
          .catch((err) => {
            reject(err);
          });
      }
    }, delay);
  });

  const unsubscribe = () => {
    cancelled = true;
  };

  (promise as any)["unsubscribe"] = unsubscribe;

  return promise as any;
}
